import React, { FC, useRef, useEffect, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface SectionEyebrowProps {
  label: string;
}

interface FeatureCardProps {
  number: string;
  title: string;
  description: string;
  delay: number;
}

interface StatItemProps {
  value: string;
  label: string;
  delay: number;
}

interface FounderCardProps {
  name: string;
  title: string;
  bio: string;
  initials: string;
  photo?: string;
  linkedin?: string;
  delay: number;
}

interface TeamMemberCardProps {
  name: string;
  role: string;
  initials: string;
  photo?: string;
  delay: number;
}

// ============================================================================
// REUSABLE COMPONENTS
// ============================================================================

/**
 * SectionEyebrow: Renders the section label with horizontal rule (no accent color)
 * Uses only black/white from existing CSS
 */
const SectionEyebrow: FC<SectionEyebrowProps> = ({ label }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '2rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.05em',
    color: '#666666',
    fontFamily: "'Lato', sans-serif",
  }}>
    <div style={{
      height: '2px',
      backgroundColor: '#191919',
      flex: '0 0 40px',
    }} />
    <span style={{ textTransform: 'uppercase' }}>{label}</span>
    <div style={{
      height: '2px',
      backgroundColor: '#191919',
      flex: '0 0 40px',
    }} />
  </div>
);

/**
 * CountUpNumber: Animates a numeric value from 0 to target on viewport entry
 */
interface CountUpNumberProps {
  value: string;
  duration?: number;
  inView: boolean;
}

const CountUpNumber: FC<CountUpNumberProps> = ({ value, duration = 2, inView }) => {
  const [displayValue, setDisplayValue] = useState('0');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!inView) return;

    // Extract numeric part from value (e.g., "3wk" -> "3", "10+" -> "10")
    const numericPart = parseInt(value.replace(/\D/g, ''), 10);
    if (isNaN(numericPart)) {
      setDisplayValue(value);
      return;
    }

    const startTime = Date.now();
    const endTime = startTime + duration * 1000;
    const updateFrequency = 16; // ~60fps

    const animate = () => {
      const now = Date.now();
      if (now >= endTime) {
        setDisplayValue(value);
        return;
      }

      const progress = (now - startTime) / (duration * 1000);
      const current = Math.floor(progress * numericPart);
      
      // Format: keep the suffix (e.g., "wk", "+")
      const suffix = value.replace(/[0-9]/g, '');
      setDisplayValue(`${current}${suffix}`);

      setTimeout(animate, updateFrequency);
    };

    animate();
  }, [inView, value, duration]);

  return (
    <div ref={ref} style={{ fontSize: '2.5rem', fontWeight: 700, fontFamily: "'Aleo', serif" }}>
      {displayValue}
    </div>
  );
};

// ============================================================================
// SECTION 1 — ABOUT MONKSTACK
// ============================================================================

const FeatureCard: FC<FeatureCardProps> = ({ number, title, description, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
      }}
      style={{
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        padding: '2rem',
        backgroundColor: '#fefefe',
        position: 'relative',
        transition: 'all 0.3s ease',
      }}
      onHoverStart={(e) => {
        const el = e.currentTarget as HTMLElement | null;
        if (el) {
          el.style.borderColor = '#191919';
          el.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)';
        }
      }}
      onHoverEnd={(e) => {
        const el = e.currentTarget as HTMLElement | null;
        if (el) {
          el.style.borderColor = '#e9ecef';
          el.style.boxShadow = 'none';
        }
      }}
    >
      <div style={{
        fontSize: '3rem',
        fontWeight: 700,
        color: '#cccccc',
        marginBottom: '1.5rem',
        fontFamily: "'Aleo', serif",
      }}>
        {number}
      </div>
      <h3 style={{
        fontSize: '1.3rem',
        fontWeight: 700,
        marginBottom: '1rem',
        color: '#191919',
        fontFamily: "'Aleo', serif",
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: '1rem',
        lineHeight: 1.6,
        color: '#666666',
        fontFamily: "'Lato', sans-serif",
        margin: 0,
      }}>
        {description}
      </p>
    </motion.div>
  );
};

const WhyMonkStackSection: FC = () => {
  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  return (
    <section
      id="why-monkstack"
      ref={sectionRef}
      style={{
        backgroundColor: '#fefefe',
        color: '#191919',
        padding: '6rem 0',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="container">
        <motion.div initial="hidden" animate={controls} variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}>
          <SectionEyebrow label="About monkstack" />
        </motion.div>

        <motion.div
          ref={titleRef}
          initial="hidden"
          animate={controls}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.1 } },
          }}
          style={{ marginBottom: '3rem' }}
        >
          <h2 className="about-section-h2" style={{
            fontWeight: 700,
            marginBottom: '1.5rem',
            color: '#191919',
            fontFamily: "'Aleo', serif",
            lineHeight: 1.2,
          }}>
            Scalable and Agile: That makes us different
          </h2>
          <p style={{
            fontSize: '1.1rem',
            lineHeight: 1.6,
            color: '#666666',
            fontFamily: "'Lato', sans-serif",
            maxWidth: '600px',
          }}>
            Five specific, tangible reasons builders and founders choose monkstack — addressing the real pain points of working with software agencies.
          </p>
        </motion.div>

        {/* Feature Cards Grid */}
        <div className="about-feature-grid">
          <FeatureCard
            number="01"
            title="You talk directly to builders"
            description="No account managers playing telephone. Direct access to the engineers writing your code, from day one."
            delay={0.2}
          />
          <FeatureCard
            number="02"
            title="AI embedded from day one"
            description="Not bolted on after the fact. We design products with intelligent automation woven into the core where it adds real value."
            delay={0.3}
          />
          <FeatureCard
            number="03"
            title="SaaS-native thinking"
            description="Multi-tenant, subscription-ready architecture. We understand the patterns that make SaaS scale, not just the features."
            delay={0.4}
          />
          <FeatureCard
            number="04"
            title="Speed without shortcuts"
            description="Fast doesn't mean fragile. Quality, security, and maintainability are baked into how we work — never traded off for velocity."
            delay={0.5}
          />

          {/* Bottom Row: Card 05 on left, Stats on right */}
          <div style={{ alignSelf: 'start' }}>
            <FeatureCard
              number="05"
              title="Outcomes over output"
              description="We measure success by what your product achieves in-market — adoption, retention, revenue — not by lines of code shipped."
              delay={0.6}
            />
          </div>

          {/* Stats Card */}
          <StatsCard />
        </div>
      </div>
    </section>
  );
};

interface StatsCardProps {
  delay?: number;
}

const StatsCard: FC<StatsCardProps> = ({ delay = 0.6 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
      }}
      style={{
        border: '1px solid #e9ecef',
        borderRadius: '12px',
        padding: '2rem',
        backgroundColor: '#fefefe',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'all 0.3s ease',
      }}
      onHoverStart={(e) => {
        const el = e.currentTarget as HTMLElement | null;
        if (el) {
          el.style.borderColor = '#191919';
          el.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.1)';
        }
      }}
      onHoverEnd={(e) => {
        const el = e.currentTarget as HTMLElement | null;
        if (el) {
          el.style.borderColor = '#e9ecef';
          el.style.boxShadow = 'none';
        }
      }}
    >
      <div style={{ marginBottom: '2rem' }}>
        <p style={{
          fontSize: '1rem',
          lineHeight: 1.7,
          color: '#333333',
          fontFamily: "'Lato', sans-serif",
          fontStyle: 'italic',
          margin: 0,
        }}>
          "Be the early ones in a small, sharp team — not a number in someone else's roadmap."
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <StatItem value="3wk" label="AVG MVP DELIVERY" delay={0.2} />
        <StatItem value="10+" label="BUILDER ENGINEERS" delay={0.3} />
        <StatItem value="40%" label="FASTER THAN TYPICAL AGENCY" delay={0.4} />
      </div>
    </motion.div>
  );
};

const StatItem: FC<StatItemProps> = ({ value, label, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <div ref={ref} style={{ display: 'flex', flexDirection: 'column' }}>
      <CountUpNumber value={value} duration={2} inView={inView} />
      <div style={{
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: '#999999',
        marginTop: '0.5rem',
        fontFamily: "'Lato', sans-serif",
      }}>
        {label}
      </div>
    </div>
  );
};

// ============================================================================
// SECTION 2 — ABOUT US
// ============================================================================

const AboutUsSection: FC = () => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  return (
    <section
      id="about-us"
      ref={sectionRef}
      style={{
        backgroundColor: '#191919',
        color: '#fefefe',
        padding: '6rem 0',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="container">
        <motion.div initial="hidden" animate={controls} variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}>
          <SectionEyebrow label="About Us" />
        </motion.div>

        <motion.h2
          className="about-section-h2"
          initial="hidden"
          animate={controls}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.1 } },
          }}
          style={{
            fontWeight: 700,
            marginBottom: '3rem',
            color: '#fefefe',
            fontFamily: "'Aleo', serif",
            lineHeight: 1.2,
          }}
        >
          Your Imagination, Our Ownership
        </motion.h2>

        <div className="about-two-col-grid">
          {/* Left Column: Text */}
          <motion.div
            initial="hidden"
            animate={controls}
            variants={{
              hidden: { opacity: 0, x: -30 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.6, delay: 0.2, staggerChildren: 0.1 },
              },
            }}
          >
            {/* Paragraph 1 */}
            <motion.p
              variants={{
                hidden: { opacity: 0, y: 10 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
              }}
              style={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: '#fefefe',
                fontFamily: "'Lato', sans-serif",
                marginBottom: '1.5rem',
              }}
            >
              We're monkstack — a team of{' '}
              <strong style={{ fontWeight: 700 }}>engineers, designers, and problem-solvers</strong> who build
              software that actually works.
            </motion.p>

            {/* Paragraph 2 */}
            <motion.p
              variants={{
                hidden: { opacity: 0, y: 10 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
              }}
              style={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: '#fefefe',
                fontFamily: "'Lato', sans-serif",
                marginBottom: '1.5rem',
              }}
            >
              We started because we saw a gap: businesses needed smart, scalable SaaS solutions but were stuck
              between bloated enterprise tools and half-baked freelancers. So we built something better — a focused
              team that combines deep technical expertise with a product-first mindset.
            </motion.p>

            {/* Paragraph 3 */}
            <motion.p
              variants={{
                hidden: { opacity: 0, y: 10 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
              }}
              style={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: '#fefefe',
                fontFamily: "'Lato', sans-serif",
                marginBottom: '2rem',
              }}
            >
              We don't just write code. We think about your users, your growth, and your bottom line — then ship
              solutions that hold up at scale.
            </motion.p>

            {/* Checklist */}
            <motion.div initial="hidden" animate={controls} variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 0.3, staggerChildren: 0.08 } } }}>
              {[
                'Speed without shortcuts. We move fast — but never at the cost of quality or security.',
                'SaaS-native thinking. Subscriptions, multi-tenancy, integrations — we know the patterns that scale.',
                'AI built in, not bolted on. Intelligent automation embedded where it adds real value.',
                'You talk to builders. Direct access to the people writing your code, every step of the way.',
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  variants={{
                    hidden: { opacity: 0, x: -10 },
                    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
                  }}
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '1rem',
                    alignItems: 'flex-start',
                  }}
                >
                  <span style={{
                    fontSize: '1.2rem',
                    color: '#fefefe',
                    flexShrink: 0,
                    marginTop: '0.25rem',
                  }}>
                    ✓
                  </span>
                  <span style={{
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    color: '#cccccc',
                    fontFamily: "'Lato', sans-serif",
                  }}>
                    {item}
                  </span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column: Stats + Avatars */}
          <motion.div
            initial="hidden"
            animate={controls}
            variants={{
              hidden: { opacity: 0, x: 30 },
              visible: {
                opacity: 1,
                x: 0,
                transition: { duration: 0.6, delay: 0.2, staggerChildren: 0.1 },
              },
            }}
          >
            {/* Stats Grid */}
            <div className="about-stats-grid">
              {[
                { value: '10+', label: 'Engineers across 3 time zones' },
                { value: '3wk', label: 'Avg. idea to working MVP' },
                { value: '40%', label: 'Faster deployment vs. industry avg' },
                { value: '100%', label: 'Builders. Zero account managers.' },
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
                  }}
                  style={{
                    padding: '1.5rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  }}
                >
                  <CountUpNumber value={stat.value} duration={2} inView={inView} />
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#999999',
                    marginTop: '0.5rem',
                    fontFamily: "'Lato', sans-serif",
                  }}>
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Avatar Row */}
            <motion.div
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
              }}>
                {['AS', 'JK', 'RT', 'MN'].map((initials, idx) => (
                  <motion.div
                    key={idx}
                    variants={{
                      hidden: { scale: 0 },
                      visible: {
                        scale: 1,
                        transition: { type: 'spring', stiffness: 200, damping: 15, delay: 0.4 + idx * 0.08 },
                      },
                    }}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: '#fefefe',
                      color: '#191919',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      fontFamily: "'Aleo', serif",
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      marginRight: idx === 3 ? '0.5rem' : 0,
                    }}
                  >
                    {initials}
                  </motion.div>
                ))}
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: '#cccccc',
                  fontFamily: "'Lato', sans-serif",
                }}>
                  +6
                </span>
              </div>

              <p style={{
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#fefefe',
                margin: '0 0 0.25rem 0',
                fontFamily: "'Aleo', serif",
              }}>
                The team you'll actually work with
              </p>
              <p style={{
                fontSize: '0.85rem',
                color: '#999999',
                margin: 0,
                fontFamily: "'Lato', sans-serif",
              }}>
                Engineers · designers · PMs · no middlemen
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// SECTION 3 — FOUNDERS
// ============================================================================

const FounderCard: FC<FounderCardProps> = ({ name, title, bio, initials, photo, linkedin, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const controls = useAnimation();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay, type: 'spring', stiffness: 100 } },
      }}
      style={{
        backgroundColor: '#fefefe',
        borderRadius: '12px',
        padding: '2rem',
        textAlign: 'center',
        border: '1px solid #e9ecef',
        transition: 'all 0.3s ease',
      }}
      onHoverStart={(e) => {
        const el = e.currentTarget as HTMLElement | null;
        if (el) {
          el.style.boxShadow = '0 12px 32px rgba(0, 0, 0, 0.15)';
          el.style.transform = 'translateY(-4px)';
        }
      }}
      onHoverEnd={(e) => {
        const el = e.currentTarget as HTMLElement | null;
        if (el) {
          el.style.boxShadow = 'none';
          el.style.transform = 'translateY(0)';
        }
      }}
    >
      <div style={{
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        margin: '0 auto 1.5rem',
        backgroundColor: '#f0f0f0',
        border: '2px solid #e9ecef',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '2rem',
        fontWeight: 700,
        color: '#191919',
        fontFamily: "'Aleo', serif",
        overflow: 'hidden',
      }}>
        {photo
          ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
          : initials
        }
      </div>

      <h3 style={{
        fontSize: '1.5rem',
        fontWeight: 700,
        margin: '0 0 0.5rem 0',
        color: '#191919',
        fontFamily: "'Aleo', serif",
      }}>
        {name}
      </h3>

      <p style={{
        fontSize: '1rem',
        fontWeight: 600,
        color: '#999999',
        margin: '0 0 1rem 0',
        fontFamily: "'Lato', sans-serif",
      }}>
        {title}
      </p>

      <div>
        <p style={{
          fontSize: '0.95rem',
          lineHeight: 1.6,
          color: '#666666',
          margin: 0,
          fontFamily: "'Lato', sans-serif",
          display: '-webkit-box',
          WebkitLineClamp: expanded ? 'unset' : 2,
          WebkitBoxOrient: 'vertical',
          overflow: expanded ? 'visible' : 'hidden',
        }}>
          {bio}
        </p>
        {bio.length > 140 && (
          <button
            onClick={() => setExpanded((prev) => !prev)}
            style={{
              marginTop: '0.5rem',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#191919',
              fontFamily: "'Lato', sans-serif",
              textDecoration: 'underline',
            }}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* LinkedIn Icon */}
      <div style={{ marginTop: '1.5rem' }}>
        <a
          href={linkedin || 'https://in.linkedin.com/company/monkstack'}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${name} on LinkedIn`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            backgroundColor: '#f0f0f0',
            color: '#191919',
            textDecoration: 'none',
            transition: 'all 0.3s ease',
            fontSize: '1.2rem',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            el.style.backgroundColor = '#191919';
            el.style.color = '#fefefe';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget;
            el.style.backgroundColor = '#f0f0f0';
            el.style.color = '#191919';
          }}
        >
          in
        </a>
      </div>
    </motion.div>
  );
};

const FoundersSection: FC = () => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  return (
    <section
      id="founders"
      ref={sectionRef}
      style={{
        backgroundColor: '#fefefe',
        color: '#191919',
        padding: '6rem 0',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="container">
        <motion.div initial="hidden" animate={controls} variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}>
          <SectionEyebrow label="Founders" />
        </motion.div>

        <motion.h2
          className="about-section-h2"
          initial="hidden"
          animate={controls}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.1 } },
          }}
          style={{
            fontWeight: 700,
            marginBottom: '3rem',
            color: '#191919',
            fontFamily: "'Aleo', serif",
            lineHeight: 1.2,
          }}
        >
          The people behind monkstack
        </motion.h2>

        <div className="about-founders-grid">
          <FounderCard
            name="Vaibhav Ramteke"
            title="Founder | CEO"
            bio="With over 15 years in the industry, our founder has worked across enterprise environments, technology startups, and a music company — a combination that shaped a rare ability to think in systems without losing sight of the product. His experience spans SaaS platforms, cloud architecture, and large-scale software — the kind of work that demands both precision and adaptability. That background is what Monkstack is built on: clear engineering, honest problem-solving, and AI applied where it actually creates value."
            initials="VR"
            photo="/img/team/vaibhav.jpeg"
            linkedin="https://www.linkedin.com/in/vaibhav-ramteke-b39601a2/"
            delay={0.2}
          />
          <FounderCard
            name="Ajay Deshpande"
            title="Chief Strategy Officer"
            bio="Ajay is a Strategy Advisor with MonkStack. He brings more than Twenty Five years of all-round experience in Software Product Development and Building Teams. His key impact areas include Product Engineering Ownership both in Startup and Enterprise Environments and Ownership of end to end SAAS products. He is also a Technology Evangelist, Thought Leader and a  Hands-on Mentor. He is an active Angel Investor and is Startup-Mentor.  He loves to teach and is an ACM Eminent Speaker."
            initials="FN"
            photo="/img/team/Ajay_Deshpande.jpg"
            linkedin="https://www.linkedin.com/in/deshpandeajay/"
            delay={0.35}
          />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// SECTION 4 — TEAM
// ============================================================================

const TeamMemberCard: FC<TeamMemberCardProps> = ({ name, role, initials, photo, delay }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
      }}
      style={{
        backgroundColor: '#fefefe',
        color: '#191919',
        borderRadius: '12px',
        padding: '1.5rem',
        textAlign: 'center',
        border: '1px solid #e9ecef',
        transition: 'all 0.3s ease',
      }}
      onHoverStart={(e) => {
        const el = e.currentTarget as HTMLElement | null;
        if (el) {
          el.style.transform = 'translateY(-4px)';
          el.style.borderColor = '#191919';
        }
      }}
      onHoverEnd={(e) => {
        const el = e.currentTarget as HTMLElement | null;
        if (el) {
          el.style.transform = 'translateY(0)';
          el.style.borderColor = '#e9ecef';
        }
      }}
    >
      <div style={{
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        margin: '0 auto 1rem',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#191919',
        fontFamily: "'Aleo', serif",
        border: '2px solid #e9ecef',
        overflow: 'hidden',
      }}>
        {photo
          ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
          : initials
        }
      </div>

      <h4 style={{
        fontSize: '1.1rem',
        fontWeight: 700,
        margin: '0 0 0.25rem 0',
        color: '#191919',
        fontFamily: "'Aleo', serif",
      }}>
        {name}
      </h4>

      <div style={{
        display: 'inline-block',
        marginTop: '0.5rem',
        marginBottom: '0',
        padding: '0.375rem 0.75rem',
        borderRadius: '20px',
        border: '1px solid #e9ecef',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: '#666666',
        fontFamily: "'Lato', sans-serif",
      }}>
        {role}
      </div>
    </motion.div>
  );
};

const TeamSection: FC = () => {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [inView, controls]);

  const teamMembers = [
  { name: 'Sapna Ramteke', role: 'Accounts & Admin Executive', initials: 'SR', photo: '/img/team/Sapna Ramteke.jpg' },
  { name: 'Amaan Hussain', role: 'Business Analyst', initials: 'AH', photo: '/img/team/AMAAN.jpeg' },
  { name: 'Jagruti Jadhav', role: 'Product Designer', initials: 'JJ', photo: '/img/team/jagruti.png' },
  { name: 'Kashif Iqbal', role: 'Team Lead', initials: 'KI', photo: '/img/team/Kashif.jpeg' },
  { name: 'Kriti Gupta', role: 'ML Engineer', initials: 'KG', photo: '/img/team/Kriti Gupta.jpg' },
  { name: 'Pooja Devrari', role: 'Associate Software Engineer', initials: 'PD', photo: '/img/team/Pooja Devrari.jpg' },
  { name: 'Vansh Ahuja', role: 'AI Engineer', initials: 'VA', photo: '/img/team/Vansh Ahuja.jpg' },
  { name: 'Udaypal Virdi', role: 'Sr. Software Engineer', initials: 'UV', photo: '/img/team/udaypal.png' },
];
  return (
    <section
      id="team"
      ref={sectionRef}
      style={{
        backgroundColor: '#191919',
        color: '#fefefe',
        padding: '6rem 0',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div className="container">
        <motion.div initial="hidden" animate={controls} variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}>
          <SectionEyebrow label="The Team" />
        </motion.div>

        <motion.h2
          initial="hidden"
          animate={controls}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.1 } },
          }}
          className="about-section-h2"
          style={{
            fontWeight: 700,
            marginBottom: '3rem',
            color: '#fefefe',
            fontFamily: "'Aleo', serif",
            lineHeight: 1.2,
          }}
        >
         One Team. Limitless Possibilities.
        </motion.h2>

        {/* Team Grid */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { delay: 0.2, staggerChildren: 0.07 },
            },
          }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '2rem',
            marginBottom: '3rem',
          }}
        >
          {teamMembers.map((member, idx) => (
            <TeamMemberCard
              key={member.name}
              name={member.name}
              role={member.role}
              initials={member.initials}
              photo={member.photo}
              delay={0.07 * idx}
            />
          ))}
        </motion.div>

        {/* Join Team Card */}
        <motion.div
          initial="hidden"
          animate={controls}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.7 } },
          }}
          style={{
            borderRadius: '12px',
            padding: '3rem 2rem',
            textAlign: 'center',
            border: '2px dashed rgba(255, 255, 255, 0.3)',
            backgroundColor: 'transparent',
            transition: 'all 0.3s ease',
          }}
          onHoverStart={(e) => {
            const el = e.currentTarget as HTMLElement | null;
            if (el) {
              el.style.borderColor = '#fefefe';
              el.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
            }
          }}
          onHoverEnd={(e) => {
            const el = e.currentTarget as HTMLElement | null;
            if (el) {
              el.style.borderColor = 'rgba(255, 255, 255, 0.3)';
              el.style.backgroundColor = 'transparent';
            }
          }}
        >
          <p style={{
            fontSize: '1.3rem',
            fontWeight: 600,
            color: '#fefefe',
            margin: '0 0 1.5rem 0',
            fontFamily: "'Aleo', serif",
          }}>
            We're always looking for builders.
          </p>
          <a
            href="/careers"
            style={{
              display: 'inline-block',
              backgroundColor: '#fefefe',
              color: '#191919',
              border: '2px solid #fefefe',
              padding: '1rem 2rem',
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '50px',
              textDecoration: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'Lato', sans-serif",
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              el.style.backgroundColor = '#191919';
              el.style.color = '#fefefe';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              el.style.backgroundColor = '#fefefe';
              el.style.color = '#191919';
            }}
          >
            See open roles →
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AboutSection: FC = () => {
  return (
    <div>
      {/* SECTION 1 — ABOUT MONKSTACK */}
      <WhyMonkStackSection />

      {/* SECTION 2 — ABOUT US */}
      <AboutUsSection />

      {/* SECTION 3 — FOUNDERS */}
      <FoundersSection />

      {/* SECTION 4 — TEAM */}
      <TeamSection />
    </div>
  );
};

export default AboutSection;
